import { Link, type LinkProps } from '@tanstack/react-router';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { PopoverContent } from '@/shared/ui/Popover/components/PopoverContent';
import { PopoverRoot } from '@/shared/ui/Popover/components/PopoverRoot';
import { PopoverTrigger } from '@/shared/ui/Popover/components/PopoverTrigger';
import { Text } from '@/shared/ui/text';

export type FlyoutItem = {
  key: string;
  label: string;
  to: NonNullable<LinkProps['to']>;
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  isActive: boolean;
  /** 非活性項目はフライアウトでも遷移不可（グレーアウト・リンク無効）にする */
  isDisabled?: boolean;
};

type Props = {
  /** トリガー要素（折りたたみ時のアイコン）。asChild で単一の子要素 */
  children: ReactNode;
  /** フライアウトに表示する項目（親リンク + 子リンク） */
  items: FlyoutItem[];
};

// マウスがトリガとコンテンツの間を移動する間、閉じないように猶予を持たせる
// 短すぎるとマウス追従が切れ、長すぎると別アイコンに移動した時に閉じ遅れを感じる
const CLOSE_DELAY_MS = 80;

/**
 * 複数の HoverFlyoutMenu が同時に開かないように、open している popover の close
 * コールバックを module スコープで管理する。新しい popover が開いたら他をすべて閉じる。
 */
const closeCallbacksById = new Map<string, () => void>();

const closeAllExcept = (excludeId: string) => {
  closeCallbacksById.forEach((close, id) => {
    if (id !== excludeId) {
      close();
    }
  });
};

/**
 * 折りたたみサイドメニュー向けのホバー連動フライアウト。
 *
 * - トリガにポインタが乗ったら開き、トリガまたはコンテンツから外れて CLOSE_DELAY_MS 後に閉じる
 * - コンテンツ内には親リンク + 子リンクをクリック可能なリンクとして表示する（Notion / BnS 風）
 * - キーボードフォーカスでも開く
 * - 別の項目にホバーしたら自分は即時閉じる（module-level singleton で連携）
 */
export const HoverFlyoutMenu = ({ children, items }: Props) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  // 即時クローズ用コールバックを module レベルで登録／解除
  useEffect(() => {
    const closeImmediately = () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setOpen(false);
    };
    closeCallbacksById.set(id, closeImmediately);
    return () => {
      closeCallbacksById.delete(id);
      // アンマウント時に保留中タイマーをクリアして leak を防ぐ
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [id]);

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  const openImmediately = () => {
    cancelClose();
    // 同時表示防止：他の popover を即時閉じる
    closeAllExcept(id);
    setOpen(true);
  };

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        onPointerEnter={openImmediately}
        onPointerLeave={scheduleClose}
        onFocus={openImmediately}
        onBlur={scheduleClose}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="center"
        sideOffset={4}
        // ホバー UI なので popover open 時に内部にフォーカスを移さない（折りたたみアイコンに focus を残す）
        onOpenAutoFocus={(event) => event.preventDefault()}
        // popover close 時にも trigger へ focus を戻さない。
        // 戻すと onFocus={openImmediately} が即発火して popover が再オープンするループになる
        // （特に popover 内 Link をクリックしたとき: close → trigger に focus 復帰 → 再オープン）
        onCloseAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={cancelClose}
        onPointerLeave={scheduleClose}
        // キーボードで Tab 移動して popover 内 Link に到達できるよう、focus が popover に
        // 入ったら close をキャンセル、外れたら close を予約する。
        // - trigger からのフォーカス遷移: trigger.onBlur で予約 → content.onFocus で取消
        // - content 内 Link 間の Tab: 前 Link.blur で予約 → 次 Link.focus で取消
        // - content 外への Tab: 最終 Link.blur で予約 → 続く focus は popover 外なので閉じる
        onFocus={cancelClose}
        onBlur={scheduleClose}
        // PopoverContent デフォルトの fade/zoom/slide アニメーションを打ち消して即時表示にする
        // （素早くアイコンを行き来した時のレスポンス向上のため）
        className={cn(
          'w-auto min-w-44 p-1',
          'data-[state=closed]:animate-none data-[state=open]:animate-none',
        )}
      >
        <nav className="flex flex-col">
          {items.map((item) => {
            const label = (
              <Text
                as="span"
                truncate
                className={cn(item.isActive ? 'text-text-base' : 'text-text-secondary')}
              >
                {item.label}
              </Text>
            );

            // 非活性項目は折りたたみ時のインライン表示と同様、リンクではなく
            // 遷移不可な div でグレーアウト表示する（フライアウト経由の遷移を防ぐ）
            if (item.isDisabled) {
              return (
                <div
                  key={item.key}
                  className="flex h-9 cursor-not-allowed items-center rounded-md px-3 opacity-40"
                >
                  {label}
                </div>
              );
            }

            return (
              <Link
                key={item.key}
                to={item.to}
                params={item.params}
                search={item.search}
                onClick={() => setOpen(false)}
                className={cn(
                  'flex h-9 cursor-pointer items-center rounded-md px-3 transition-all duration-200',
                  item.isActive ? 'bg-surface-strength2' : 'hover:bg-[rgba(12,10,9,0.04)]',
                )}
              >
                {label}
              </Link>
            );
          })}
        </nav>
      </PopoverContent>
    </PopoverRoot>
  );
};
